import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { usersApi } from '@/api/users'
import { bulkApi, type BulkOperationResult } from '@/api/bulk'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import type { User } from '@/types'
import { BulkResultSummary } from './BulkResultSummary'

interface AssignWordsToStudentsDialogProps {
  open: boolean
  wordIds: number[]
  onClose: () => void
  /** Вызывается после успешного назначения — страница может сбросить выделение. */
  onAssigned?: () => void
}

/**
 * Назначение выбранных слов ученикам — у каждого появится карточка.
 *
 * Тегов здесь нет: они принадлежат слову и ставятся в разделе слов
 * (см. `.cursor/rules/easya-tags-belong-to-words.mdc`).
 */
export function AssignWordsToStudentsDialog({
  open,
  wordIds,
  onClose,
  onAssigned,
}: AssignWordsToStudentsDialogProps) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [students, setStudents] = useState<User[]>([])
  const [selectedStudents, setSelectedStudents] = useState<User[]>([])
  const [skipExisting, setSkipExisting] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkOperationResult | null>(null)

  // Данные грузятся после открытия: модалка показывается сразу, со скелетоном.
  useEffect(() => {
    if (!open) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setResult(null)

    void (async () => {
      try {
        const list = await usersApi.listAllStudents()
        if (!cancelled) setStudents(list)
      } catch (err) {
        if (!cancelled) {
          setError(displayErrorMessage(getErrorMessage(err), t('words.bulk.loadError')))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, t])

  const handleClose = () => {
    setSelectedStudents([])
    setSkipExisting(true)
    setError(null)
    setResult(null)
    onClose()
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const report = await bulkApi.assignWordsToStudents({
        word_ids: wordIds,
        student_ids: selectedStudents.map((s) => s.id),
        skip_existing: skipExisting,
      })
      setResult(report)
      onAssigned?.()
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('words.bulk.assignError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('words.bulk.assignToStudents')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {result ? (
          <BulkResultSummary result={result} />
        ) : loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('words.bulk.assignHint', { count: wordIds.length })}
            </Typography>

            <Autocomplete
              multiple
              options={students}
              value={selectedStudents}
              onChange={(_, value) => setSelectedStudents(value)}
              getOptionLabel={(option) =>
                `${option.surname ?? ''} ${option.first_name ?? ''}`.trim() || option.email
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label={t('words.bulk.students')} size="small" />
              )}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={skipExisting}
                  onChange={(e) => setSkipExisting(e.target.checked)}
                />
              }
              label={t('words.bulk.skipExisting')}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {result ? t('common.close', 'Закрыть') : t('common.cancel', 'Отмена')}
        </Button>
        {!result && (
          <Button
            variant="contained"
            disabled={submitting || loading || selectedStudents.length === 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? t('common.saving', 'Сохранение...') : t('words.bulk.assign')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
